import FormBuilderModel from '../../../../common/models/tracked/formBuilderModel';
import FormElement from "./formElement"
import {FormElementType} from "~/enums/formElementType";
import ThirdPartyCompleter from "./templates/thirdPartyCompleter";
import {RelationType} from "../../../../common/models/relationType";
import {Capability} from "./formElementCapability";

export default class ElementContainer extends FormBuilderModel {
  element_type: FormElementType;
  form_elements: {[client_id: string]: FormElement};
  except: string[] = ['form', 'form_elements', 'createEmbeddedObject'];

  constructor() {
    super();
    this.form_elements = {};
  }

  createEmbeddedObject = {
    form_elements: (parent, json): FormElement => { return this.rootModel.createFormElement(json.element_type); },
  };

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'form_elements': { type: FormElement, relationType: RelationType.dictionary }
    };
  }

  get formElementsArray(): any[] {
    if (!this.form_elements) return [];
    
    return Object.keys(this.form_elements)
      .map(key => this.form_elements[key])
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  get formElementsArrayDeep(): FormElement[] {
    const elements = [];
    this.forEachFormElement(el => elements.push(el), true);
    return elements;
  }

  get isEmpty(): boolean {
    return this.formElementsArray.length === 0;
  }

  get hasControls(): boolean {
    return !this.isEmpty;
  }

  nextChildSortOrder(): number {
    let position = 0;
    this.formElementsArray.forEach((element, i) => {
        if (i >= position) {
          position = i + 1;
        }
      });
    return position;
  }

  updateElementPositions(movedElement, newPosition): void {
    this.formElementsArray
      .filter(element => element !== movedElement)
      .sort((a, b) => a.sort_order - b.sort_order)
      .forEach((element, i) => {
        element.sort_order = i;
        if (element.sort_order >= newPosition) {
          element.sort_order += 1;
        }
      });
  }

  recalculateElementPositions() {
    this.formElementsArray
        .sort((a, b) => a.sort_order - b.sort_order)
        .forEach((element, i) => {
          element.sort_order = i;
        });
  }

  get thirdPartyElements(): ThirdPartyCompleter[] {
    const elements = this.thirdPartyElementsImmediate.slice();
    this.formElementsArray.forEach((fe: FormElement) => {
      elements.push(...fe.thirdPartyElements);
    });
    return elements;
  }

  get thirdPartyElementsImmediate(): ThirdPartyCompleter[] {
    return this.formElementsArray.filter(fe => fe.element_type === FormElementType.thirdParty);
  }

  get lookupFieldElements(): FormElement[] {
    const elements = this.lookupFieldElementsImmediate.slice();
    this.formElementsArray.forEach((fe: FormElement) => {
      elements.push(...fe.lookupFieldElements);
    });
    return elements;
  }

  get lookupFieldElementsImmediate(): FormElement[] {
    return this.formElementsArray.filter(fe => fe.element_type === FormElementType.lookupField);
  }

  forEachFormElement(callback: (el: FormElement) => void, recurse: boolean = false): void {
    for (const key in this.form_elements) {
      callback(this.form_elements[key]);
      if (recurse) {
        this.form_elements[key].forEachFormElement(callback, recurse);
      }
    }
  }

  referenceableFormElementsDeep(includeRepeatables = false, {excludeSingleLayoutElements}: { excludeSingleLayoutElements?: boolean } = {}): FormElement[] {
    const matchers = [
      // must be referenceable
      element => element.can(Capability.referenceable)
    ]
    if (excludeSingleLayoutElements) {
      matchers.push(element => {
        // if element is "grid" or similar, then only allow repeatable ones
        if (element.can(Capability.layoutContainer)) {
          return element.is_repeatable
        } else {
          return true
        }
      })
    }

    if (includeRepeatables){
      return this.formElementsArrayDeep.filter(element => {
        return matchers.every(matcher => matcher(element));
      });
    }else{
      return this.nonRepeatableFormElementsDeep.filter(element => {
        return matchers.every(matcher => matcher(element));
      });
    }

  }

  subreferenceableFormElementsDeep(includeRepeatables = false, filter?: (el: FormElement) => boolean): FormElement[] {
    const filters: Array<(el: FormElement) => boolean> = [
      // must be referenceable
      el => el.can(Capability.referenceable) || el.can(Capability.subreferenceable),
      // can't be a form element nested inside an image upload element
      // since those elements just store cropping data
      el => {
        if (!(el.parent instanceof FormElement)) return true
        return el.parent.element_type !== FormElementType.imageUpload
      },
    ]
    if (typeof filter === 'function') filters.push(filter)
    const allFilters: (el: FormElement) => boolean = (el) => filters.every(f => f(el))
    if (includeRepeatables){
      return this.formElementsArrayDeep.filter(allFilters)
    } else {
      return this.nonRepeatableFormElementsDeep.filter(allFilters);
    }
  }

  get nonRepeatableFormElementsDeep(): FormElement[] {
    return this.formElementsArrayDeep.filter(element => {
      // must not be repeatable and must not be in a repeatable
      return !element.is_repeatable && element.repeatableAncestors.length === 0;
    });
  }

  get uploadImageFormElements(): FormElement[] {
    return this.formElementsArrayDeep.filter(element => {
      // must be image upload element type
      return element.elementTypeName === "image upload"
    });
  }

  findElement(id: string): FormElement {
    let found: FormElement
    for (const element of Object.values(this.form_elements)) {
      if (element.id === id || element.client_id === id) found = element
      if (found) break
      found = element.findElement(id);
      if (found) break
    }
    return found
  }
}
